<template>
    <div class="naef-bg" id="login">
        <main class="flex flex-1 flex-col items-center pb-10 pt-[126px] md:pt-[180px]">
            <!-- Carte principale -->
            <div class="naef-carte p-9">
                <!-- Verrouillage de marque : logo Naef Projets Neufs au-dessus
                     du lockup du portail, comme sur la maquette du design system. -->
                <img
                    class="mx-auto h-[52px] w-auto"
                    src="@/assets/imgs/naef-projets-neufs-logo.svg"
                    alt="Naef Projets Neufs"
                />
                <img
                    class="mx-auto mt-4 h-[34px] w-auto"
                    src="@/assets/imgs/naef-logo.svg"
                    alt="e-promotion"
                />

                <template v-if="!connected">
                    <h1 class="mt-8 text-center font-display font-semibold text-ds-display text-nuit-noire">
                        {{ showDemoForm ? "Demander un accès démo" : "Connexion à votre espace" }}
                    </h1>
                    <p class="mt-2 text-center text-ds-body text-nuit-noire">
                        <template v-if="showDemoForm">
                            Laissez-nous vos coordonnées, nous vous ouvrons un accès
                            de démonstration.
                        </template>
                        <template v-else>
                            Pour suivre la commercialisation de vos promotions,
                            veuillez vous identifier.
                        </template>
                    </p>

                    <!-- Connexion -->
                    <form
                        v-if="!showDemoForm"
                        action="#"
                        method="POST"
                        class="mt-8"
                        @submit.prevent="handleLoginClick"
                    >
                        <label for="email" class="block text-ds-body text-nuit-noire">Identifiant ou e-mail</label>
                        <input
                            v-model="email"
                            dusk="email"
                            id="email"
                            :type="emailFieldType"
                            required
                            class="form-input-ds mt-1"
                        />

                        <label for="password" class="mt-4 block text-ds-body text-nuit-noire">Mot de passe</label>
                        <input
                            v-model="password"
                            dusk="password"
                            id="password"
                            type="password"
                            required
                            class="form-input-ds mt-1"
                        />

                        <button
                            type="submit"
                            dusk="submit"
                            class="btn-ds btn-ds--primary mt-6 w-full"
                            :class="{ 'is-loading': loading }"
                            :disabled="loading"
                        >
                            {{ loading ? "..." : "Se connecter" }}
                        </button>

                        <label class="mt-5 flex cursor-pointer items-center gap-2 text-ds-body text-nuit-noire">
                            <input v-model="remember" dusk="remember" type="checkbox" class="case-ds" />
                            Se souvenir de moi
                        </label>

                        <hr class="mt-8 border-quartz" />

                        <router-link
                            :to="{ name: 'password-reset' }"
                            class="mt-4 inline-block text-ds-small text-nuit-noire hover:text-rouge-groseille focus:outline-none focus:underline"
                            >Mot de passe oublié ?</router-link
                        >
                    </form>

                    <!-- Demande d'acces demo -->
                    <form v-else class="mt-8" @submit.prevent="requestDemoAccess">
                        <label for="fullName" class="block text-ds-body text-nuit-noire">Prénom Nom</label>
                        <input v-model="fullName" id="fullName" type="text" required class="form-input-ds mt-1" />

                        <label for="demoEmail" class="mt-4 block text-ds-body text-nuit-noire">E-mail</label>
                        <input v-model="demoEmail" id="demoEmail" type="email" required class="form-input-ds mt-1" />

                        <label for="phone" class="mt-4 block text-ds-body text-nuit-noire">Téléphone</label>
                        <input v-model="phone" id="phone" type="tel" required class="form-input-ds mt-1" />

                        <button type="submit" class="btn-ds btn-ds--primary mt-6 w-full">
                            Confirmer
                        </button>
                    </form>
                </template>

                <!-- Deja connecte -->
                <div v-else class="mt-8 text-center">
                    <p class="text-ds-body text-nuit-noire">
                        Merci de vous déconnecter si vous souhaitez changer de compte.
                    </p>
                    <button class="btn-ds btn-ds--secondary mt-4 w-full" @click="logout">
                        Se déconnecter
                    </button>
                </div>
            </div>

            <!-- Pastille de separation -->
            <div v-if="!connected" class="naef-ou" aria-hidden="true">
                <span>OU</span>
            </div>

            <!-- Carte secondaire -->
            <div v-if="!connected" class="naef-carte">
                <button
                    type="button"
                    class="w-full px-9 py-7 text-center text-ds-body text-nuit-noire hover:text-rouge-groseille focus:outline-none"
                    @click="toggleDemoForm"
                >
                    {{ showDemoForm ? "Retour à la connexion" : "Demander un accès démo" }}
                </button>
            </div>
        </main>

        <mentions-legales />
    </div>
</template>

<script>
import Cookies from "js-cookie";
import Redirect from "@/mixins/Redirect";
import Auth from "@/mixins/Auth";
import auth from "@/api/auth";
import MentionsLegales from "@/components/global/MentionsLegales";

export default {
    components: { MentionsLegales },
    mixins: [Redirect, Auth],
    data() {
        return {
            email: "",
            password: "",
            fullName: "",
            demoEmail: "",
            phone: "",
            remember: false,
            loading: false,
            showDemoForm: false, // État pour gérer le formulaire affiché
        };
    },
    computed : {
        demoDemo(){
            return this.email == "DEMO" && this.password == "DEMO";
        },
        emailFieldType(){
            return this.email == "DEMO" ? "text" : "email";
        }
    },
    mounted(){
        // Pour se connecter en DEMO, on passe forcément par un URL Query param
        const demoUserType = this.$route.query.demo;
        if(demoUserType){
            const body = {
                email : `${demoUserType}.demo@naef.ch`,
                password : "NaefNaefDemo!"
            };
            localStorage.setItem("demo", demoUserType);
            this.login(body, true);
        }
    },
    methods: {
        toggleDemoForm() {
            this.showDemoForm = !this.showDemoForm;
        },
        startLoading(){
            this.loading = true;
        },
        stopLoading(){
            this.loading = false;
        },
        handleLoginClick() {
            this.startLoading();

            if(this.demoDemo){
                this.$router.push({name : "demo"});
                return;
            }
            localStorage.removeItem("demo");

            const body = {
                email: this.email,
                password: this.password,
            };

            this.login(body);
        },
        login(body, remember = this.remember){
            Cookies.remove("token");
            return this.$store.dispatch("auth/login", body).then((loginResponse) => {
                if (!loginResponse.status || loginResponse.status === 404) {
                    this.$toast.open({
                        type: "error",
                        message: "E-mail ou mot de passe incorrect",
                    });
                    this.stopLoading();
                    return;
                }

                this.$store
                    .dispatch("auth/user")
                    .then((res) => {
                        if(res.status == 200){
                            
                            let token = this.$store.getters["auth/token"];
                            // « Se souvenir de moi » : cookie conservé 30 jours ;
                            // sinon cookie de session, effacé à la fermeture du
                            // navigateur. L'API ne gère pas cette option (jeton
                            // JWT valable une semaine dans tous les cas).
                            Cookies.set("token", token, remember ? { expires: 30 } : {});
                            
                            let redirectLink = localStorage.getItem("redirectAfterLogin");
    
                            this.$store.dispatch("app/filters").then(() => {
                                if(redirectLink){
                                    this.$toast.open({
                                        type: "info",
                                        message: "Redirection...",
                                    });
                                    setTimeout(() => {
                                        localStorage.removeItem("redirectAfterLogin");
                                        window.location.href = redirectLink;
                                    }, 2000);
                                }
                                else{
                                    this.redirectHome();
                                }
                            });
                        }
                        this.stopLoading();
                    })
                    .catch((err) => {
                        this.stopLoading();
                        this.$toast.open({
                            type: "error",
                            message: "Une erreur est survenue",
                        });
                        throw err;
                    });
            });
        },
        requestDemoAccess() {
            // Logique pour la demande d'accès démo
            const demoData = {
                fullName: this.fullName,
                email: this.demoEmail,
                phone: this.phone,
            };

            auth.askDemo(demoData)
                .then(() => {
                    this.$toast.open({
                        type: "success",
                        message: "Vous recevrez sous peu un accès démo par mail",
                    });
                    this.stopLoading();
                })
                .catch(error => {
                    this.stopLoading();
                    this.$toast.open({
                        type: "error",
                        message: error.response.data.error
                    });
                });

            // Réinitialisation et retour au formulaire de connexion
            this.fullName = "";
            this.demoEmail = "";
            this.phone = "";
            this.showDemoForm = false;
        },
    },
    watch: {
        $route: {
            immediate: true,
            handler() {
                document.title = "E-Promotion | Login";
            }
        },
    },       
};
</script>
